import React from "react";
import { Outlet } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";

import PlatformAppBar from "../../../components/layout/PlatformAppBar";
import PlatformFooter from "../../../components/layout/PlatformFooter";
import AdminPlatformSidebar from "../../../components/layout/admin/AdminPlatformSidebar";

import "./AdminLayout.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const ADMIN_REFRESH_INTERVAL_MS = 20 * 60 * 1000;

export default function AdminLayout() {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  const [admin, setAdmin] = React.useState(null);

  const [isLoadingAdmin, setIsLoadingAdmin] = React.useState(true);

  const [adminError, setAdminError] = React.useState("");

  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const lastLoadedAtRef = React.useRef(0);

  /* =====================================================
     LOAD ADMIN PROFILE
     ===================================================== */

  const loadAdmin = React.useCallback(
    async ({ silent = false } = {}) => {
      if (!isAuthenticated) {
        setAdmin(null);
        setIsLoadingAdmin(false);
        return;
      }

      try {
        if (!silent) {
          setIsLoadingAdmin(true);
        }

        setAdminError("");

        const accessToken = await getAccessTokenSilently();

        const response = await fetch(`${API_BASE}/api/admin/me/`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          console.error("Failed to load /api/admin/me/", data);

          setAdminError("Failed to load administrator data.");

          setAdmin(null);

          return;
        }

        setAdmin(data);

        lastLoadedAtRef.current = Date.now();
      } catch (error) {
        console.error("Failed to load /api/admin/me/", error);

        setAdminError("Failed to load administrator data.");

        setAdmin(null);
      } finally {
        if (!silent) {
          setIsLoadingAdmin(false);
        }
      }
    },
    [isAuthenticated, getAccessTokenSilently],
  );

  /* =====================================================
     INITIAL LOAD
     ===================================================== */

  React.useEffect(() => {
    loadAdmin();
  }, [loadAdmin]);

  /* =====================================================
     PERIODIC REFRESH
     ===================================================== */

  React.useEffect(() => {
    if (!isAuthenticated) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        loadAdmin({
          silent: true,
        });
      }
    }, ADMIN_REFRESH_INTERVAL_MS);

    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") {
        return;
      }

      const now = Date.now();

      const msSinceLastLoad = now - lastLoadedAtRef.current;

      if (msSinceLastLoad >= ADMIN_REFRESH_INTERVAL_MS) {
        loadAdmin({
          silent: true,
        });
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);

      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isAuthenticated, loadAdmin]);

  /* =====================================================
     DISPLAY INFORMATION
     ===================================================== */

  const organisation = admin?.organisation || {};

  const fullName =
    `${admin?.first_name || ""} ${admin?.last_name || ""}`.trim() ||
    "Administrator";

  const initials =
    `${admin?.first_name?.[0] || ""}${admin?.last_name?.[0] || ""}`
      .toUpperCase()
      .trim() || "A";

  /* =====================================================
     RENDER
     ===================================================== */

  return (
    <div className="admin-dashboard-page">
      <PlatformAppBar
        actorType="PLATFORM_ADMIN"
        fullName={fullName}
        initials={initials}
        notificationCount={0}
        homePath="/"
        onOpenMobileMenu={() => setMobileMenuOpen(true)}
        avatarImageUrl=""
        avatarFit="contain"
      />

      <div className="admin-dashboard-main">
        <AdminPlatformSidebar
          mobileOpen={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
        />

        <div className="admin-dashboard-content-shell">
          {!admin && isLoadingAdmin ? (
            <div className="admin-layout-loading">
              <div className="admin-layout-loading__card">
                Loading administrator dashboard...
              </div>
            </div>
          ) : adminError && !admin ? (
            <div className="admin-layout-loading">
              <div
                className="
                  admin-layout-loading__card
                  admin-layout-loading__card--error
                "
              >
                {adminError}
              </div>
            </div>
          ) : (
            <Outlet
              context={{
                admin,
                organisation,
                refreshAdmin: loadAdmin,
                isLoadingAdmin,
              }}
            />
          )}
        </div>
      </div>

      <PlatformFooter />
    </div>
  );
}
